import React, {useEffect, useState} from 'react'
import classNames from 'classnames'
import {AutoPaySettings, PaymentAccount, Property} from '../../types/app-types'
import {findAutoPaySettings, formatDayOfMonth} from '../../services/utils'
import AlertMessage from '../common/AlertMessage'
import Alert from '../common/Alert'
import PaymentAmountInput from '../payment/PaymentAmountInput'

interface AutoPaySettingsModalProps {
  paymentAccount: PaymentAccount
  property: Property
  accountId: string
  isAutoPayChanging: boolean
  isOpen: boolean
  onClose: () => void
  onSave: (autoPaySettings: AutoPaySettings) => void
}

const AutoPaySettingsModal = (props: AutoPaySettingsModalProps) => {
  const {
    accountId,
    isAutoPayChanging,
    isOpen,
    onClose,
    onSave,
    paymentAccount,
    property
  } = props

  const defaultAutoPaySettings = {
    autoPayEnabled: true,
    autoPayAmount: 0,
    autoPayDayOfMonth: 1
  }
  const [autoPaySettings, updateAutoPaySettings] = useState(
    defaultAutoPaySettings
  )
  const {autoPayAmount, autoPayDayOfMonth} = autoPaySettings
  const dayOfMonthOptions = [1, 2, 3, 4, 5]

  useEffect(() => {
    const currentSettings = findAutoPaySettings(paymentAccount, accountId)

    const settings = Object.assign({}, defaultAutoPaySettings, currentSettings)
    updateAutoPaySettings(settings)
  }, [paymentAccount, accountId])

  return (
    <div
      className={classNames({
        modal: true,
        'modal-open': isOpen
      })}
    >
      <div className="modal-box p-8">
        <h1 className="modal-header">
          {isAutoPayChanging ? 'Enable AutoPay' : 'Edit AutoPay Settings'}
        </h1>
        {isAutoPayChanging ? (
          <AlertMessage
            type="warning"
            className="mb-2"
            message={`Are you sure you want to change the AutoPay account?  
            This will disable automatic payments for the current AutoPay account.`}
          />
        ) : (
          <Alert type="info" className="mb-6 w-full">
            <p>
              {`Please note: an additional ${
                property.creditCardFee * 100
              }% transaction fee will be charged
              for credit or debit card payments.`}
            </p>
          </Alert>
        )}
        <form>
          <div className="form-control">
            <PaymentAmountInput
              amount={autoPayAmount}
              label="AutoPay Amount"
              maxAmount={10000}
              disable={false}
              onPaymentAmountChange={amount => {
                updateAutoPaySettings({
                  ...autoPaySettings,
                  autoPayAmount: amount
                })
              }}
            />

            <span className="label-text-alt">
              Please enter a maximum monthly payment amount. If your remaining
              balance at the time of AutoPay is lower, only the remaining
              balance will be charged.
            </span>
          </div>
          <div className="form-control mt-4">
            <label className="label">
              <span className="label-text">AutoPay Day of Month</span>
            </label>
            <select
              className="select select-primary"
              onChange={e => {
                const selection = dayOfMonthOptions[e.target.selectedIndex]
                updateAutoPaySettings({
                  ...autoPaySettings,
                  autoPayDayOfMonth: selection
                })
              }}
              value={autoPayDayOfMonth}
            >
              {dayOfMonthOptions.map(x => {
                return (
                  <option key={x} value={x}>
                    {formatDayOfMonth(x)}
                  </option>
                )
              })}
            </select>
            <span className="label-text-alt">
              Payments will be applied on this day of every month.
            </span>
          </div>
        </form>
        <div className="modal-action">
          <button
            className="btn btn-outline w-28"
            onClick={e => {
              onClose()
            }}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary w-28"
            onClick={e => onSave(autoPaySettings)}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  )
}

export default AutoPaySettingsModal
